import { useContext, useEffect, useState, useRef } from "react"
import { PeFilterContext } from "../../Contexts/PeFilterContext"
import Popup from "../Common/Popup"
import Constants from "../../Constants"
import axios from "axios"
import { refineSearchSubFirmTypeUrl } from "../../Config/config"
import { UserContext } from "../../Contexts/UserContext"

function FirmTypeFilter({
  setFirmTypeOptions,
  setFirmSubTypeOptions,
  FirmTypeOptions,
  customPathname,
  dataPath,
  label = "Firm Type",
  isPopupOpen,
  setIsPopupOpen
}) {
  const { firmTypeResponseData, refineSearchFilters, setSubFirmType } = useContext(PeFilterContext)
  const { getToken } = useContext(UserContext)
  const [internalPopupOpen, setInternalPopupOpen] = useState(false);
  const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
  const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

  const [peSubTypes, setPeSubTypes] = useState([])
  const [vcSubTypes, setVcSubTypes] = useState([])

  const [combinedSubTypes, setCombinedSubTypes] = useState([])

  const [expandedFirmType, setExpandedFirmType] = useState(null)

  const peScrollRef = useRef(null)
  const vcScrollRef = useRef(null)

  const [peScrollPosition, setPeScrollPosition] = useState(0)
  const [vcScrollPosition, setVcScrollPosition] = useState(0)

  const [loadingSubTypes, setLoadingSubTypes] = useState(false)
  const [peInteracted, setPeInteracted] = useState(false)
  const [vcInteracted, setVcInteracted] = useState(false)
  const [pendingExpand, setPendingExpand] = useState(null) // firmType id waiting to expand

  useEffect(() => {
    const industryIds = refineSearchFilters[customPathname]?.firmType?.map((value) => value.id) ?? []
    setFirmTypeOptions(
      firmTypeResponseData[dataPath]?.map((industry) => ({
        ...industry,
        isChecked: industryIds.includes(industry.id),
      })),
    )
  }, [customPathname, dataPath, firmTypeResponseData, refineSearchFilters, setFirmTypeOptions])

  useEffect(() => {
    if (peScrollRef.current && expandedFirmType === 1) {
      peScrollRef.current.scrollTop = peScrollPosition
    }
    if (vcScrollRef.current && expandedFirmType === 2) {
      vcScrollRef.current.scrollTop = vcScrollPosition
    }
  }, [peSubTypes, vcSubTypes, expandedFirmType, peScrollPosition, vcScrollPosition])

  useEffect(() => {
    const fetchAllSubTypes = async () => {
      setLoadingSubTypes(true)
      try {
        const response = await axios.post(
          refineSearchSubFirmTypeUrl,
          { firm_type: "All" },
          { headers: { Authorization: getToken() } }
        )

        if (response.status === 200) {
          const selectedSubTypeIds =
            refineSearchFilters[customPathname]?.firm_Sub_type?.map((value) => value.id) ?? []
          const allSubTypes = response.data?.firmType ?? []

          const hasPE = FirmTypeOptions?.some((type) => type.id === 1)
          const hasVC = FirmTypeOptions?.some((type) => type.id === 2)

          if (!hasPE && !hasVC) {
            setPeSubTypes([])
            setVcSubTypes([])
            setCombinedSubTypes([])
            setFirmSubTypeOptions([])
            setLoadingSubTypes(false)
            return
          }

          const peSubTypesArray = []
          const vcSubTypesArray = []

          allSubTypes.forEach((subType) => {
            const displayName = subType.name.replace(/\s*\((PE|VC)\)/g, "").trim()

            const cleanedSubType = {
              ...subType,
              displayName,
              originalName: subType.name,
              isChecked: selectedSubTypeIds.includes(subType.id),
            }

            if (subType.name.includes("(PE)")) {
              peSubTypesArray.push(cleanedSubType)
            } else if (subType.name.includes("(VC)")) {
              vcSubTypesArray.push(cleanedSubType)
            } else {
              peSubTypesArray.push(cleanedSubType)
            }
          })

          setPeSubTypes(peSubTypesArray)
          setVcSubTypes(vcSubTypesArray)

          const combined = [
            ...peSubTypesArray.map((type) => ({ ...type, firmType: "PE" })),
            ...vcSubTypesArray.map((type) => ({ ...type, firmType: "VC" })),
          ]
          setCombinedSubTypes(combined)
          setSubFirmType(allSubTypes)

          setFirmSubTypeOptions(combined)
        }
      } catch (error) {
        console.error("Error fetching sub-types:", error)
      }
      setLoadingSubTypes(false)
    }

    fetchAllSubTypes()
  }, [
    customPathname,
    getToken,
    refineSearchFilters,
    setFirmSubTypeOptions,
    setFirmTypeOptions,
    setSubFirmType,
    FirmTypeOptions,
  ])

  useEffect(() => {
    const selectedFirmTypes = FirmTypeOptions?.filter((industry) => industry.isChecked) || []
    const hasPE = selectedFirmTypes.some((type) => type.id === 1)
    const hasVC = selectedFirmTypes.some((type) => type.id === 2)
  }, [FirmTypeOptions])

  // when user requested expand via arrow click, wait until subtypes are loaded
  useEffect(() => {
    if (!pendingExpand) return

    const firmId = pendingExpand

    const isPe = firmId === 1
    const isVc = firmId === 2

    const subTypesLoaded = (isPe && (peSubTypes?.length > 0 || !loadingSubTypes)) || (isVc && (vcSubTypes?.length > 0 || !loadingSubTypes))

    if (subTypesLoaded) {
      setExpandedFirmType(firmId)
      setPendingExpand(null)
    }
  }, [pendingExpand, peSubTypes, vcSubTypes, loadingSubTypes])

  const toggleExpand = (firmTypeId, e) => {
    e.stopPropagation()
    setExpandedFirmType(expandedFirmType === firmTypeId ? null : firmTypeId)
  }

  const handleFirmTypeChange = (e) => {
    const { name, checked, id } = e.target

    if (name === "allSelect") {
      const currentFirmTypes = FirmTypeOptions.map((type) => ({ ...type, isChecked: checked }))
      setFirmTypeOptions(currentFirmTypes)
      setExpandedFirmType(null)
    } else {
      const currentFirmTypes = FirmTypeOptions.map((type) => {
        const isNumeric = /^[0-9]+$/.test(id)
        const parsedId = isNumeric ? Number.parseInt(id) : id
        return type.id === parsedId ? { ...type, isChecked: checked } : type
      })

      setFirmTypeOptions(currentFirmTypes)

      const numericId = Number.parseInt(id)

      if (!checked && expandedFirmType === numericId) {
        setExpandedFirmType(null)
      }

      if (checked && expandedFirmType !== numericId) {
        setExpandedFirmType(null)
      }

      if (!checked) {
        if (numericId === 1) {
          setPeSubTypes([])
          setPeInteracted(true)
        } else if (numericId === 2) {
          setVcSubTypes([])
          setVcInteracted(true)
        }
      }
    }
  }

  const handleSubTypeChange = (firmTypeId) => (e) => {
    e.stopPropagation()

    // mark that the user has interacted with sub-types for this firm type
    if (firmTypeId === 1) setPeInteracted(true)
    if (firmTypeId === 2) setVcInteracted(true)

    if (firmTypeId === 1 && peScrollRef.current) {
      setPeScrollPosition(peScrollRef.current.scrollTop)
    } else if (firmTypeId === 2 && vcScrollRef.current) {
      setVcScrollPosition(vcScrollRef.current.scrollTop)
    }

    const { name, checked, id } = e.target

    const currentSubTypes = firmTypeId === 1 ? peSubTypes : vcSubTypes

    let updatedList = []

    if (name === "allSelect") {
      updatedList = currentSubTypes.map((item) => ({
        ...item,
        isChecked: checked,
      }))
    } else if (name === "filteredSelect" && e.target.filteredIds) {
      updatedList = currentSubTypes.map((item) =>
        e.target.filteredIds.includes(item.id) ? { ...item, isChecked: checked } : item,
      )
    } else {
      const actualId = e.target.dataset.subtypeId ? Number.parseInt(e.target.dataset.subtypeId) : Number.parseInt(id)
      updatedList = currentSubTypes.map((item) => (item.id === actualId ? { ...item, isChecked: checked } : item))
    }

    if (firmTypeId === 1) {
      setPeSubTypes(updatedList)
    } else {
      setVcSubTypes(updatedList)
    }

    const otherSubTypes = firmTypeId === 1 ? vcSubTypes : peSubTypes
    const combined = [
      ...(firmTypeId === 1 ? updatedList : otherSubTypes).map((type) => ({ ...type, firmType: "PE" })),
      ...(firmTypeId === 2 ? updatedList : otherSubTypes).map((type) => ({ ...type, firmType: "VC" })),
    ]

    setCombinedSubTypes(combined)

    setFirmSubTypeOptions(combined)
  }

  const handleSelectAll = (firmTypeId) => (e) => {
    e.stopPropagation()
    const checked = e.target.checked
    
    if (firmTypeId === 1) {
      const updatedPeSubTypes = peSubTypes.map(item => ({ ...item, isChecked: checked }))
      setPeSubTypes(updatedPeSubTypes)
      const combined = [
        ...updatedPeSubTypes.map(type => ({ ...type, firmType: "PE" })),
        ...vcSubTypes.map(type => ({ ...type, firmType: "VC" }))
      ]
      setCombinedSubTypes(combined)
      setFirmSubTypeOptions(combined)
    } else if (firmTypeId === 2) {
      const updatedVcSubTypes = vcSubTypes.map(item => ({ ...item, isChecked: checked }))
      setVcSubTypes(updatedVcSubTypes)
      const combined = [
        ...peSubTypes.map(type => ({ ...type, firmType: "PE" })),
        ...updatedVcSubTypes.map(type => ({ ...type, firmType: "VC" }))
      ]
      setCombinedSubTypes(combined)
      setFirmSubTypeOptions(combined)
    }
  }

  const firmTypeSelectedCount = FirmTypeOptions?.filter((option) => option.isChecked)?.length ?? 0
  const peSubTypeSelectedCount = peSubTypes?.filter((option) => option.isChecked)?.length ?? 0
  const vcSubTypeSelectedCount = vcSubTypes?.filter((option) => option.isChecked)?.length ?? 0
  const totalSubTypesSelectedCount = peSubTypeSelectedCount + vcSubTypeSelectedCount

  const StyledMultiSelectEntity = ({ handleEntityChange, primaryEntity, firmTypeId }) => {
    const scrollRef = firmTypeId === 1 ? peScrollRef : vcScrollRef

    return (
      <div className="multiselect-container">
        
        <div className="entity-list" style={{ maxHeight: "8rem", overflowY: "auto" }} ref={scrollRef}>
          {loadingSubTypes ? (
            <></>
          ) : primaryEntity && primaryEntity.length > 0 ? (
            <>
              <div 
                className="form-check font-sans_medium_body cursor-pointer flex text-left py-2 pr-2 w-full font-medium text-gray-700 border-b border-gray-100 hover:bg-gray-50"
                onClick={(e) => {
                  const isChecked = primaryEntity.every(entity => entity.isChecked)
                  handleSelectAll(firmTypeId)({
                    ...e,
                    target: { ...e.target, checked: !isChecked }
                  })
                }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  name="allSelect"
                  id={`select-all-${firmTypeId}`}
                  checked={primaryEntity.every(entity => entity.isChecked)}
                  onChange={(e) => handleSelectAll(firmTypeId)(e)}
                />
                <label 
                  htmlFor={`select-all-${firmTypeId}`}
                  className="form-check-label mx-2 mr-2 cursor-pointer text-[13px] text-[#333333] leading-[17px] font-sans_book_body flex-grow"
                >
                  All
                </label>
              </div>
              {primaryEntity.map((entity, index) => (
                <div
                  className="form-check font-sans_medium_body cursor-default flex text-left py-2 pr-2 w-full font-medium text-gray-700 border-b border-gray-100 hover:bg-gray-50"
                  key={entity.id || index}
                >
                  <input
                    type="checkbox"
                    className="form-check-input"
                    name={entity.name.replace(/\s*\((PE|VC)\)/g, "").trim()}
                    id={`subtype-${firmTypeId}-${entity.id}`}
                    data-subtype-id={entity.id}
                    checked={entity?.isChecked || false}
                    onChange={handleEntityChange}
                  />
                  <label
                    htmlFor={`subtype-${firmTypeId}-${entity.id}`}
                    className="form-check-label mx-2 mr-2 cursor-pointer text-[13px] text-[#333333] leading-[17px] font-sans_book_body"
                  >
                    {entity.displayName}
                  </label>
                </div>
              ))}
            </>
          ) : (
            <></>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full md:inline-block">
      <button
        type="button"
        className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100  text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
        onClick={() => setPopupOpen(!popupOpen)}
      >
        <span className="truncate pr-2">{label} <span className="opacity-60 ml-2 text-xs text-ellipsis">e.g. {FirmTypeOptions.slice(0,2).map(item => item.name).join(', ')}</span></span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {firmTypeSelectedCount > 0 && (
            <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
              {firmTypeSelectedCount}
            </span>
          )}
          <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
            ›
          </span>
        </div>
      </button>

      {popupOpen && (
        <Popup onClose={() => setPopupOpen(false)} label={label}>
          <div className="p-4">
            <div className="flex flex-col">
              {FirmTypeOptions?.map((firmType) => (
                <div key={firmType.id} className="mb-2">
                  <div className="flex items-center">
                    <div className="form-check font-sans_medium_body cursor-default flex items-center text-left py-2 pr-2 w-full font-medium text-gray-700 border-b border-gray-100 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        name={firmType.name}
                        id={firmType.id}
                        checked={firmType?.isChecked || false}
                        onChange={handleFirmTypeChange}
                      />

                      <label
                        htmlFor={firmType.id}
                        className="form-check-label mx-2 mr-2 cursor-pointer text-[13px] text-[#333333] leading-[17px] font-sans_book_body flex items-center"
                      >
                        {firmType.name.replace(/$$[^)]*$$/g, "").trim()}

                        {(firmType.name === 'PE' || firmType.name === 'VC') && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              // if not checked, programmatically check and expand
                              if (!firmType.isChecked) {
                                const updated = (FirmTypeOptions || []).map((type) =>
                                  type.id === firmType.id ? { ...type, isChecked: true } : type,
                                )
                                setFirmTypeOptions(updated)
                                if (firmType.id === 1) setPeInteracted(true)
                                if (firmType.id === 2) setVcInteracted(true)
                                // set pending expand until data loads
                                setPendingExpand(firmType.id)
                              } else {
                                // already checked: toggle expand/collapse
                                toggleExpand(firmType.id, e)
                              }
                            }}
                            className="ml-3 text-[#BC8B20] text-xs flex items-center"
                          >
                            {expandedFirmType === firmType.id ? "▼" : "▶"}
                            {firmType.id === 1 && (
                              <span className="ml-1">{peInteracted ? peSubTypeSelectedCount : peSubTypes?.length ?? 0}</span>
                            )}
                            {firmType.id === 2 && (
                              <span className="ml-1">{vcInteracted ? vcSubTypeSelectedCount : vcSubTypes?.length ?? 0}</span>
                            )}
                          </button>
                        )}
                      </label>
                    </div>
                  </div>

                  {firmType.isChecked && expandedFirmType === firmType.id && (
                    <div className="ml-6 border-l-2 pl-2 border-[#E8E6E1] mt-2">
                      {firmType.id === 1 && (
                        <StyledMultiSelectEntity
                          handleEntityChange={handleSubTypeChange(1)}
                          primaryEntity={peSubTypes}
                          firmTypeId={1}
                        />
                      )}
                      {firmType.id === 2 && (
                        <StyledMultiSelectEntity
                          handleEntityChange={handleSubTypeChange(2)}
                          primaryEntity={vcSubTypes}
                          firmTypeId={2}
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Popup>
      )}
    </div>
  )
}

export default FirmTypeFilter